// E8IsoscelesTrianglePerimeterAreaOpenTask.js
// Zadanie otwarte E8 (2025 CZE zad. 20) - trojkat rownoramienny: obwod i ramie dluzsze
// od podstawy o stala -> pole trojkata (tw. Pitagorasa dla wysokosci).

import React from "react";
import { BlockMath, InlineMath } from "../../../../components/math/KatexWrappers";
import EquationInput from "../../../../components/inputs/EquationInput";
import FractionInput from "../../../../components/inputs/FractionInput";
import { scoreStaged } from "../../../openTaskScoring";

const CASES = [
  { perimeter: 36, diff: 3 }, // base=10, arm=13, h=12, pole=60 (original)
  { perimeter: 50, diff: 1 }, // base=16, arm=17, h=15, pole=120
  { perimeter: 72, diff: 6 }, // base=20, arm=26, h=24, pole=240
  { perimeter: 64, diff: 11 }, // base=14, arm=25, h=24, pole=168
];

function pick(array) {
  return array[Math.floor(Math.random() * array.length)];
}

function buildData(config) {
  const base = (config.perimeter - 2 * config.diff) / 3;
  const arm = base + config.diff;
  const half = base / 2;
  const height = Math.sqrt(arm * arm - half * half);
  const area = (base * height) / 2;
  return { ...config, base, arm, half, height, area, maxPoints: 3 };
}

function createOriginalTask() {
  return {
    type: "E8IsoscelesTrianglePerimeterAreaOpenTask",
    data: buildData(CASES[0]),
    source: { variant: "original", exam: "E8", session: "2025_CZE", taskNumber: 20 },
    tags: ["e8", "planimetria", "trojkat-rownoramienny", "pitagoras", "pole", "zadanie-otwarte"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  return {
    type: "E8IsoscelesTrianglePerimeterAreaOpenTask",
    data: buildData(pick(CASES.slice(1))),
    source: { variant: "random", basedOn: "E8_2025_CZE_20" },
    tags: ["e8", "planimetria", "trojkat-rownoramienny", "pitagoras", "pole", "zadanie-otwarte"],
  };
}

function rawValue(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value.raw === "string") return value.raw;
  return String(value);
}

function normalize(raw) {
  return rawValue(raw).toLowerCase().replace(/,/g, ".").replace(/\s+/g, "");
}

function numberTokens(raw) {
  return (normalize(raw).match(/\d+(?:\.\d+)?/g) || []).map(Number);
}

function hasNumber(raw, target, tolerance = 0.01) {
  return numberTokens(raw).some((value) => Math.abs(value - target) <= tolerance);
}

function parseNumberLike(value) {
  const raw = normalize(value);
  if (!raw) return null;
  const number = Number(raw);
  return Number.isFinite(number) ? number : null;
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Obwód trójkąta równoramiennego jest równy <InlineMath math={`${data.perimeter}\\text{ cm}`} />.
        Ramię tego trójkąta jest o <InlineMath math={`${data.diff}\\text{ cm}`} /> dłuższe od jego podstawy.
      </p>
      <p><strong>Oblicz pole tego trójkąta. Zapisz obliczenia.</strong></p>
    </div>
  );
}

export function renderInput({ data }, userAnswer = {}, onChange, disabled) {
  const setField = (field) => (value) => onChange({ ...(userAnswer || {}), [field]: value });
  return (
    <div style={{ display: "grid", gap: 14, textAlign: "left" }}>
      <div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Krok 1. Oblicz długości boków (podstawa i ramię).</div>
        <EquationInput value={userAnswer.sides || ""} onChange={setField("sides")} disabled={disabled} placeholder="np. 2(x+3)+x=36, x=10, ramię=13" showPreview={false} basis="420px" />
      </div>
      <div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Krok 2. Oblicz wysokość (tw. Pitagorasa).</div>
        <EquationInput value={userAnswer.height || ""} onChange={setField("height")} disabled={disabled} placeholder="np. h^2+5^2=13^2, h=12" showPreview basis="420px" />
      </div>
      <div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Krok 3. Wpisz pole trójkąta.</div>
        <FractionInput suffixLatex="\\text{cm}^2" value={userAnswer.area} onChange={setField("area")} disabled={disabled} placeholder="np. 60" />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer = {}) {
  const sidesOk = hasNumber(userAnswer.sides, data.base) && hasNumber(userAnswer.sides, data.arm);
  const heightOk = hasNumber(userAnswer.height, data.height);
  const result = parseNumberLike(userAnswer.area);
  const areaOk = result != null && Math.abs(result - data.area) <= 0.01;
  return scoreStaged({
    resultOk: areaOk,
    milestones: [sidesOk, heightOk],
    maxScore: data.maxPoints,
    parts: { "boki trójkąta": sidesOk, "wysokość": heightOk, "pole": areaOk },
  });
}

export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>{"Przyk\u0142adowa poprawna odpowied\u017a:"}</div>
      <BlockMath math={`2(x+${data.diff})+x=${data.perimeter}`} />
      <BlockMath math={`x=${data.base},\\quad x+${data.diff}=${data.arm}`} />
      <BlockMath math={`h^2+${data.half}^2=${data.arm}^2,\\quad h=${data.height}`} />
      <BlockMath math={`P=\\frac{${data.base}\\cdot ${data.height}}{2}=${data.area}\\text{ cm}^2`} />
      <div>{"Odpowied\u017a: pole tr\u00f3jk\u0105ta jest r\u00f3wne "}<InlineMath math={`${data.area}\\text{ cm}^2`} />{"."}</div>
    </div>
  );
}

export function renderUserAnswer(userAnswer = {}) {
  const result = parseNumberLike(userAnswer.area);
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>Boki: {userAnswer.sides || <i>(brak)</i>}</div>
      <div>Wysokość: {userAnswer.height || <i>(brak)</i>}</div>
      <div>Pole: {result == null ? <i>(brak)</i> : <InlineMath math={`${result}\\text{ cm}^2`} />}</div>
    </div>
  );
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Oznaczmy podstawę przez <InlineMath math="x" />, wtedy ramię ma
        długość <InlineMath math={`x+${data.diff}`} />. Z obwodu:
      </p>
      <BlockMath math={`2(x+${data.diff})+x=${data.perimeter}\\implies 3x=${data.perimeter - 2 * data.diff}\\implies x=${data.base}`} />
      <p>Podstawa ma <InlineMath math={`${data.base}\\text{ cm}`} />, a ramię <InlineMath math={`${data.arm}\\text{ cm}`} />.</p>
      <p><strong>Krok 2.</strong> Wysokość opuszczona na podstawę dzieli ją na połowy. Z twierdzenia Pitagorasa:</p>
      <BlockMath math={`h^2+${data.half}^2=${data.arm}^2\\implies h^2=${data.arm * data.arm}-${data.half * data.half}=${data.arm * data.arm - data.half * data.half}\\implies h=${data.height}`} />
      <p><strong>Krok 3.</strong> Obliczamy pole trójkąta:</p>
      <BlockMath math={`P=\\frac{${data.base}\\cdot ${data.height}}{2}=${data.area}\\text{ cm}^2`} />
    </div>
  );
}
